import {FormAction, FormState, FormStateWithIssues} from '@raison/form-models';
import {expectEither, HttpClient, useHttpClient} from '@raison/network-facade';
import infoIcon from '@raison/static/icons/information-line.svg';
import questionIcon from '@raison/static/icons/question-line.svg';
import {doNothing, getKeysUnsafe, invariant} from '@raison/toolbox';
import {
    RootLayout,
    RsnAmount,
    RsnAmountInput,
    RsnButton,
    RsnDialog,
    RsnInput,
    RsnMessage,
    RsnRadioGroup,
    RsnTextarea,
    RsnTextButton,
    useBoolean,
} from '@raison/ui-kit';
import {QueryClient, QueryKey, useQuery} from '@tanstack/react-query';
import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import * as Context from 'effect/Context';
import {TaggedError} from 'effect/Data';
import * as Effect from 'effect/Effect';
import {absurd, identity} from 'effect/Function';
import * as Layer from 'effect/Layer';
import {isUndefined} from 'effect/Predicate';
import * as Record from 'effect/Record';
import React, {Dispatch, Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {useCurrencyFormat, usePrimaryCurrencyFormat, useRateToPrimaryCurrency} from '~/entities/currencies';
import {InfoPopup} from '~/entities/dialogs';
import {OperationDetail, OperationSelectButton} from '~/entities/operations-fiat';
import {BlockingCryptoTransactsContainer} from '~/entities/transactions-history';
import {FeeRequestData, fetchFeesOnSend} from '../api/fetchFeesOnSend';
import {QrScannerButton} from '../lib/QrScannerOverlay';
import {useInHistoryStateNamespace} from '../lib/useInHistoryState';
import {SendTxResources} from '../model/SendTxContainer';
import {useSendTxResources} from '../model/SendTxResourcesContext';

export type SendTxFlowType = (typeof SendTxFlowType)[keyof typeof SendTxFlowType];
export const SendTxFlowType = {
    intro: 'intro',
    usual: 'usual',
    travelRule: 'tr',
} as const;

export interface SendTxIntroResult extends CalculatedAmounts {
    flow: Exclude<SendTxFlowType, 'intro'>;
    address: string;
    comment: string;
    feeCalculationType: FeeCalculationType;
}

const Dialog = type({
    name: "'fee-handling'",
}).or({
    name: "'fee-issue'",
    message: () => type.enumerated(IssueOnFee.NotEnough),
});

export function SendTxIntro({
    formState,
    dispatchToForm,
    onNextStep,
}: {
    onNextStep: (results: SendTxIntroResult) => void;
    dispatchToForm: Dispatch<FormState.Action<Values>>;
    formState: SendTxIntroForm;
}) {
    const resources = useSendTxResources();
    const {asset, amountLimit} = resources;

    const actOnForm = FormAction.bindAllActions(dispatchToForm);

    const isAtUpperLimit = (amount: RsnAmount) => RsnAmount.toBigNumber(amount).isEqualTo(amountLimit.upper);

    let isAllUsed = false;
    formState = FormState.withRecomputedValues(formState, values => {
        isAllUsed = isAtUpperLimit(values.amount);
        if (isAllUsed) {
            return {...values, feeCalculationType: FeeCalculationType.Included};
        } else {
            return values;
        }
    });

    const {amounts, asyncIssues, syncIssues, flow, isValidating, flushChecks, checkDoneAt, checkInitAt} =
        useSendFlowService(formState, resources);

    const {format: formatAsSubject} = useCurrencyFormat(asset);
    const {format: formatAsPrimary} = usePrimaryCurrencyFormat({approx: true});
    const {format: formatAsSubjectBlockchain} = useCurrencyFormat(asset.blockchain);
    const renderAsSubject = renderAsyncOperationDetail(isValidating, formatAsSubject);
    const renderAsSubjectBlockchain = renderAsyncOperationDetail(isValidating, formatAsSubjectBlockchain);
    const renderAsPrimary = renderAsyncOperationDetail(isValidating, formatAsPrimary);

    const renderIssues = (issues: Issues): Record<keyof Issues, string | undefined> => ({
        address: (() => {
            switch (issues.address) {
                case IssueOnAddress.Required:
                    return t.Required;
                case IssueOnAddress.Incorrect:
                    return t.IncorrectAddress;
                default:
                    return identity<undefined>(issues.address);
            }
        })(),
        comment: (() => {
            switch (issues.comment) {
                case IssueOnComment.MaxLength:
                    return t.MaxDetails;
                default:
                    return identity<undefined>(issues.comment);
            }
        })(),
        amount: (() => {
            switch (issues.amount) {
                case IssueOnAmount.Required:
                    return t.Required;
                case IssueOnAmount.NotEnough:
                    return t.NotEnough;
                case IssueOnAmount.MinNonZero:
                    return t.MinNonZero;
                case IssueOnAmount.MinLimit:
                    return t.MinLimit({amount: formatAsSubject(amountLimit.lower)});
                default:
                    return identity<undefined>(issues.amount);
            }
        })(),
        feeCalculationType: issues.feeCalculationType,
    });

    const {
        hasSubmitIssues,
        hasIssues,
        fields,
        onSubmit: onSubmitIntent,
    } = FormState.selectFormView(
        FormStateWithIssues.identity({
            ...formState,
            submitDoneAt: checkDoneAt,
            submitInitAt: checkInitAt,
            submitIssues: renderIssues(asyncIssues ?? {}),
            issues: renderIssues(syncIssues ?? {}),
        }),
        {
            dispatch: dispatchToForm,
            submit() {
                if (!flow || !amounts) {
                    return false;
                }
                onNextStep({flow, ...amounts, ...FormState.toValues(formState)});
            },
        }
    );

    const feeIssue = asyncIssues?.feeCalculationType;
    const canSubmit = amounts && !hasIssues && !hasSubmitIssues && !isValidating;

    const rate = useRateToPrimaryCurrency(asset);

    const act = {
        useAllClicked() {
            actOnForm.fieldChanged('amount', RsnAmount.fromBigNumber(amountLimit.upper));
        },
        qrScanned(qrData: string) {
            const {address, amount} = interpretQrResult(qrData);
            actOnForm.fieldsSetAutomatically([
                ['address', address], //
                amount && ['amount', RsnAmount.fromString(amount)],
            ]);
        },
        clickedNextStep() {
            flushChecks();
            if (canSubmit) {
                onSubmitIntent();
            } else {
                actOnForm.submitAttempt();
            }
        },
    };

    const isOnOwnBlockchain = asset.code === asset.blockchain.currencyCode;

    const [isModalOpen, setModalOpen] = useBoolean(false);

    const navigate = useNavigate();
    const useInHistoryState = useInHistoryStateNamespace('send_intro', navigate);
    const [dialog, setDialog] = useInHistoryState(Dialog);

    const formElement = (
        <form className='contents' onSubmit={ev => (ev.preventDefault(), act.clickedNextStep())}>
            {isOnOwnBlockchain && resources.asset.locked.gt(0) && (
                <Fragment>
                    <RsnMessage.Notification type='button' onClick={setModalOpen.on} arrowed centered>
                        <b className='fv-text-m-bold'>
                            {resources.asset.locked.toFixed(8)} {resources.asset.code}
                        </b>
                        {' is temporarily locked until the last transaction is completed'}
                    </RsnMessage.Notification>

                    <BlockingCryptoTransactsContainer
                        from={resources.asset.id}
                        isOpen={isModalOpen}
                        onClose={setModalOpen.off}
                    />
                </Fragment>
            )}

            <OperationSelectButton
                label={t.From}
                iconSrc={asset.icon}
                heading={asset.name}
                details={t.Balance({formattedBalance: formatAsSubject(amountLimit.upper)})}
            />

            {asset.type === 'stablecoin' && (
                <OperationSelectButton
                    label='Fee'
                    iconSrc={asset.blockchain.icon}
                    heading={asset.blockchain.name}
                    details={t.Balance({formattedBalance: formatAsSubjectBlockchain(amountLimit.fee)})}
                />
            )}

            <RsnTextarea
                label={t.ToAccount}
                decoration={<QrScannerButton onScan={act.qrScanned} />}
                maxLength={42}
                rows={2}
                {...fields.address}
            />

            <RsnInput
                label={<RsnInput.Label hint={t.DetailsHint}>{t.Details}</RsnInput.Label>}
                maxLength={maxCommentLength}
                helperText={t.DetailsHelper}
                {...fields.comment}
            />

            {asset.type === 'crypto' && (
                <div className='mt-4'>
                    <RsnRadioGroup
                        label={
                            <RsnRadioGroup.Label
                                icon={{svg: questionIcon, onClick: () => setDialog({name: 'fee-handling'})}}
                            >
                                {t.FeeHandling}
                            </RsnRadioGroup.Label>
                        }
                        disabled={isAllUsed}
                        options={[
                            {value: FeeCalculationType.Included, label: 'Included'},
                            {value: FeeCalculationType.OnTop, label: 'On top'},
                        ]}
                        value={fields.feeCalculationType.value}
                        onChange={fields.feeCalculationType.onChange}
                    />
                </div>
            )}

            <div className='mt-auto flex flex-col gap-4'>
                <RsnAmountInput
                    {...fields.amount}
                    suffix={asset.code}
                    maxDecimals={asset.decimals}
                    maxIntegers={10}
                    hintAction={<HintActionUseAll onClick={act.useAllClicked} used={isAllUsed} />}
                    helperText={
                        amountLimit.lower.isZero()
                            ? t.MinNonZero
                            : t.MinLimit({amount: formatAsSubject(amountLimit.lower)})
                    }
                />

                <OperationDetail.Layout>
                    <OperationDetail
                        label={t.TransactionFee}
                        {...(feeIssue !== undefined && {
                            icon: {src: infoIcon, onClick: () => setDialog({name: 'fee-issue', message: feeIssue})},
                            highlight: true,
                        })}
                    >
                        {renderAsSubjectBlockchain(amounts?.feeAmount)}
                    </OperationDetail>
                    {isOnOwnBlockchain && (
                        <OperationDetail label={t.YouSpend}>{renderAsSubject(amounts?.amountToBeSent)}</OperationDetail>
                    )}
                    <OperationDetail label={t.ApproxValue}>
                        {renderAsPrimary(amounts?.amountToBeSent.multipliedBy(rate))}
                    </OperationDetail>
                    <OperationDetail.Total label={t.BeneficiaryGets}>
                        {renderAsSubject(amounts?.amountToBeReceived)}
                    </OperationDetail.Total>
                </OperationDetail.Layout>

                <RsnButton.Primary type='submit'>{isValidating ? t.Checking : t.NextStep}</RsnButton.Primary>
            </div>
        </form>
    );
    return (
        <RootLayout common title={`Send ${resources.asset.code}`}>
            {formElement}

            <RsnDialog.Controller onClose={() => navigate(-1)}>
                {(() => {
                    switch (dialog?.name) {
                        case 'fee-handling':
                            return <FeeHandlingDialog />;
                        case 'fee-issue':
                            return <FeeIssueDialog message={dialog.message} />;
                        default:
                            return null;
                    }
                })()}
            </RsnDialog.Controller>
        </RootLayout>
    );
}

function renderAsyncOperationDetail(isFetching: boolean, formatter: (value: BigNumber.Value) => string) {
    // eslint-disable-next-line react/display-name
    return (value: BigNumber.Value | undefined) => {
        if (isFetching) {
            return <OperationDetail.Skeleton />;
        }
        if (value === undefined) {
            return formatter(0);
        }
        return formatter(value);
    };
}

const maxCommentLength = 140;
const t = {
    Required: 'This field is required',
    MaxDetails: `Maximum ${maxCommentLength} characters`,
    Buy: interpolate(`Buy {{currencyCode}}`),
    NotEnough: 'Not enough funds',
    NotEnouthForFee: interpolate('You don’t have enough {{currencyCode}} to cover the transaction fee.'),
    MinLimit: interpolate('Min: {{amount}}'),
    MinNonZero: 'Amount must be greater than zero',
    From: 'From',
    ToAccount: 'To account',
    Details: 'Details',
    DetailsHint: '(remittance information)',
    DetailsHelper: 'For your reference only — not visible to the recipient',
    ApproxValue: 'Approx value',
    NextStep: 'Next step',
    Checking: 'Checking...',
    Balance: interpolate('Balance: {{formattedBalance}}'),
    YouSpend: 'You spend',
    FeeHandling: 'Fee handling',
    TransactionFee: 'Transaction fee',
    BeneficiaryGets: 'Beneficiary gets',
    GotIt: 'I got it',
    HowTheTransferFeeAffects: 'How the transfer fee affects what you pay and what the recipient gets.',
    Included: 'Included',
    IncludedExplanation: 'Fee is included in the amount you enter. The beneficiary gets less.',
    IncorrectAddress: 'Incorrect adddress',
    OnTop: 'On top',
    OnTopExplanation: 'Fee is added on top of the amount you enter. The beneficiary gets the full amount.',
};

/**
 * Ethereum WEI is one 18-th of a ETH
 */
const ETH_ATOMIC_UNIT_FRACTION = 1e18;

function interpretQrResult(result: string): QrScanValues {
    if (!URL.canParse(result)) {
        return {address: result};
    }
    const url = new URL(result);

    // spec: https://en.bitcoin.it/wiki/BIP_0021
    if (url.protocol === 'bitcoin:') {
        const address = url.pathname;
        const amount = url.searchParams.get('amount');
        return {address, ...(amount ? {amount} : {})};
    }

    // spec: https://github.com/ethereum/ercs/blob/master/ERCS/erc-681.md
    if (url.protocol === 'ethereum:') {
        const indexOfPrefixEnd = url.pathname.indexOf('-'); // slice off the "pay-" or other prefix
        const indexOfAddressEnd = Math.min(url.pathname.indexOf('@'), url.pathname.indexOf('/')); // ignore chain_id and function_name
        const prefixEnd = indexOfPrefixEnd > -1 ? indexOfPrefixEnd : 0;
        const addressEnd = indexOfAddressEnd > -1 ? indexOfAddressEnd : undefined;
        const address = url.pathname.slice(prefixEnd, addressEnd);

        // some qr generators put amount not in `value` field as per spec, but in `amount` like for btc
        const amountRaw = url.searchParams.get('value') || url.searchParams.get('amount') || NaN;
        const amountBnAtomic = BigNumber(amountRaw);
        const amount = amountBnAtomic.isNaN() ? null : amountBnAtomic.dividedBy(ETH_ATOMIC_UNIT_FRACTION).toFormat();

        return {address, ...(amount ? {amount: amount} : null)};
    }
    return {address: result};
}

interface QrScanValues {
    address: string;
    amount?: string;
}

export enum FeeCalculationType {
    OnTop = 'on_top',
    Included = 'included',
}

export interface CalculatedAmounts {
    feeAmount: BigNumber;
    amountToBeSent: BigNumber;
    amountToBeReceived: BigNumber;
}

type Values = SendTxIntroValues;
export type SendTxIntroValues = ReturnType<typeof SendTxIntroValues>;
export const SendTxIntroValues = () => ({
    address: '',
    amount: RsnAmount.empty(),
    comment: '',
    feeCalculationType: FeeCalculationType.Included,
});
SendTxIntroValues.fieldNames = identity<Array<keyof SendTxIntroValues>>([
    'address',
    'amount',
    'comment',
    'feeCalculationType',
]);

export type SendTxIntroForm = ReturnType<typeof SendTxIntroForm>;
export const SendTxIntroForm = () => FormState.from(SendTxIntroValues());

enum IssueOnAmount {
    Required = 'REQUIRED',
    NotEnough = 'NOT_ENOUGH',
    MinNonZero = 'MIN_NON_ZERO',
    MinLimit = 'MIN_LIMIT',
}
enum IssueOnComment {
    MaxLength = 'MAX_LENGTH',
}
enum IssueOnFee {
    NotEnough = 'NOT_ENOUGH_FOR_FEE',
}
enum IssueOnAddress {
    Required = 'REQUIRED',
    Incorrect = 'INCORRECT',
}

interface Issues {
    amount?: IssueOnAmount;
    address?: IssueOnAddress;
    comment?: IssueOnComment;
    feeCalculationType?: IssueOnFee;
}
namespace Issues {
    export const isEmpty = (issues: Issues) => Object.values(issues).every(isUndefined);
}

function useFeeRequestData(values: Values, resources: SendTxResources) {
    const accountId = resources.asset.id;
    const accountType = resources.asset.type;
    const address = values.address;
    const feeCalculationType = values.feeCalculationType;
    const amount = RsnAmount.useMemo(values.amount);

    return useMemo<FeeRequestData>(
        () => ({
            accountId,
            accountType,
            address,
            feeCalculationType,
            amount: RsnAmount.toBigNumber(amount),
        }),
        [accountId, accountType, address, amount, feeCalculationType]
    );
}

const HttpClientTag = Context.GenericTag<HttpClient>('HttpClient');
const ResourcesTag = Context.GenericTag<SendTxResources>('SendTxResources');
const FeeRequestDataTag = Context.GenericTag<FeeRequestData>('FeeRequestData');
const QueryFnContext = Context.GenericTag<{
    client: QueryClient;
    queryKey: QueryKey;
    signal: AbortSignal;
}>('QueryFnContext');

const cancelQuery = Effect.gen(function* () {
    const {client, signal, queryKey} = yield* QueryFnContext;
    return yield* Effect.promise(async () => {
        await client.cancelQueries({queryKey}, {revert: true, silent: true});
        invariant(signal.aborted, 'after client.cancelQueries signal must be aborted');
        throw signal.reason;
    });
});

const getAmounts = Effect.gen(function* () {
    const http = yield* HttpClientTag;
    const data = yield* FeeRequestDataTag;
    const response = yield* Effect.tryPromise(signal => fetchFeesOnSend(http, data, {signal}));
    if (response.status !== 200) {
        switch (response.status) {
            case 400:
                return yield* new SendIntroIssues({issues: interpretServerIssues(response.json)});
            case 'Abort':
                return yield* new HttpAbort(response);
            default:
                return absurd<never>(response);
        }
    }
    return response.json;
});

const getAmountsOrIssues = getAmounts.pipe(
    Effect.map(amounts => ({amounts, issues: undefined})),
    Effect.catchTags({
        SendIntroIssues: ({issues}) => Effect.succeed({issues, amounts: undefined}),
    })
);

const getFlow = Effect.gen(function* () {
    const http = yield* HttpClientTag;
    const data = yield* FeeRequestDataTag;
    const response = yield* Effect.tryPromise(signal =>
        http(`v1/travel_rule/enabled?address=${data.address}`, {signal}).then(
            ...expectEither({
                200: type({enabled: 'boolean'}),
            })
        )
    );

    if (response.status !== 200) {
        switch (response.status) {
            case 'Abort':
                return yield* new HttpAbort(response);
            default:
                return absurd<never>(response);
        }
    }
    const {
        json: {enabled: isInTravelRule},
    } = response;
    if (isInTravelRule) {
        return SendTxFlowType.travelRule;
    } else {
        return SendTxFlowType.usual;
    }
});

const callSendService = Effect.gen(function* () {
    const [flow, calculation] = yield* Effect.all([getFlow, getAmountsOrIssues], {concurrency: 2});
    const {amounts} = calculation;
    let {issues} = calculation;
    if (amounts) {
        const fee = yield* validateFee(amounts);
        if (fee) {
            issues = {...issues, feeCalculationType: fee};
        }
    }
    return {flow, amounts, issues};
});

const validateFields = (values: Values, resources: SendTxResources) => {
    const issues: Issues = {};
    const amountIssues = validateAmountField(values.amount, resources);
    if (amountIssues) {
        issues.amount = amountIssues;
    }

    if (values.address === '') {
        issues.address = IssueOnAddress.Required;
    }
    return issues;
};

const validateFee = (amounts: CalculatedAmounts) =>
    Effect.gen(function* () {
        const {amountLimit} = yield* ResourcesTag;
        if (
            amounts.feeAmount.isGreaterThan(amountLimit.fee) ||
            amounts.amountToBeSent.isGreaterThan(amountLimit.upper)
        ) {
            return IssueOnFee.NotEnough;
        }
    });

function validateAmountField(encodedAmount: RsnAmount, {amountLimit}: SendTxResources) {
    const amount = RsnAmount.toBigNumber(encodedAmount);
    if (amount.isNaN()) {
        return IssueOnAmount.Required;
    }
    if (amountLimit.lower.eq(0) && amount.eq(0)) {
        return IssueOnAmount.MinNonZero;
    }
    if (amountLimit.lower.gt(amount)) {
        return IssueOnAmount.MinLimit;
    }
    if (amountLimit.upper.lt(amount)) {
        return IssueOnAmount.NotEnough;
    }
}

function interpretServerIssues(
    rawIssues: {amount?: string; address?: string; feeCalculationType?: string} | undefined
) {
    const issues: Issues = {};
    if (!rawIssues || Object.keys(rawIssues).length === 0) {
        return issues;
    }
    if (rawIssues.address) {
        if (rawIssues.address.startsWith('Incorrect address')) {
            issues.address = IssueOnAddress.Incorrect;
        }
        // TODO: add absurd clause, backend shouldn't send unfamiliar strings
    }
    if (rawIssues.amount) {
        if (rawIssues.amount.startsWith('Not enough')) {
            issues.amount = IssueOnAmount.NotEnough;
        }
        if (rawIssues.amount === 'This value should not be blank.') {
            issues.amount = undefined; // handled by syncIssues
        }
        if (rawIssues.amount === 'This value should be greater than or equal to 0.') {
            issues.amount = IssueOnAmount.MinNonZero;
        }
        // TODO: add absurd clause, backend shouldn't send unfamiliar strings
    }
    if (rawIssues.feeCalculationType) {
        if (rawIssues.feeCalculationType.startsWith('Not enough')) {
            issues.feeCalculationType = IssueOnFee.NotEnough;
        }
    }
    return issues;
}

function useSendFlowService(formState: FormState<Values>, resources: SendTxResources) {
    const values = FormState.toValues(formState);
    const http = useHttpClient();

    const syncIssues = validateFields(values, resources);
    const shouldQuery = isUndefined(syncIssues.address);

    const requestData = useFeeRequestData(values, resources);
    const [debouncedRequestData, {isDebouncing, flush}] = useDebounce(500, requestData);

    const {dataUpdatedAt, isFetching, data} = useQuery({
        enabled: shouldQuery && !isDebouncing,
        queryKey: ['CalculatedAmounts', debouncedRequestData] as const,
        queryFn({queryKey: [, data], queryKey, client, signal}) {
            const layer = Layer.mergeAll(
                Layer.succeed(HttpClientTag, http),
                Layer.succeed(FeeRequestDataTag, data),
                Layer.succeed(ResourcesTag, resources),
                Layer.succeed(QueryFnContext, {queryKey, client, signal})
            );

            const checkInitAt = Date.now();
            const query = callSendService.pipe(
                Effect.catchTags({
                    HttpAbort: () => cancelQuery,
                    UnknownException: exc => Effect.die(exc.error),
                }),
                Effect.provide(layer),
                Effect.map(result => ({
                    ...result,
                    checkInitAt,
                }))
            ) satisfies Effect.Effect<
                unknown,
                never // ensure all errors are handled explicitly
            >;
            return Effect.runPromise(query, {signal});
        },
    });

    const isValidating = isFetching || isDebouncing;

    const {flow, amounts, issues, checkInitAt = 0} = data ?? {};
    const asyncIssues = isValidating ? undefined : {...issues, ...interpretServerIssues(formState.submitIssues)};

    const checkDoneAt = dataUpdatedAt || checkInitAt;

    return {
        amounts,
        flow,
        syncIssues,
        asyncIssues,
        isValidating,
        checkInitAt,
        checkDoneAt,
        flushChecks: flush,
    };
}

class SendIntroIssues extends TaggedError('SendIntroIssues')<{issues: Issues}> {}
class HttpAbort extends TaggedError('HttpAbort')<{reason: unknown}> {}

export function useDebounce<T>(delay: number, value: T) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    const isDebouncing = !Object.is(value, debouncedValue);

    const flushRef = useRef(doNothing);
    const flushCb = useCallback(() => {
        flushRef.current();
    }, []);

    useEffect(() => {
        const flush = () => {
            cleanup();
            setDebouncedValue(value);
        };
        flushRef.current = flush;
        const timeout = setTimeout(flush, delay);

        const cleanup = () => {
            flushRef.current = doNothing;
            clearTimeout(timeout);
        };
        return cleanup;
    }, [value]);

    return [debouncedValue, {isDebouncing, flush: flushCb}] as const;
}

function FeeHandlingDialog() {
    const cls = {
        p: 'fv-text-m-regular text-neutral-90',
        h: 'fv-text-l-bold text-neutral-100',
    };
    return (
        <RsnDialog.Window placement={'center'}>
            <RsnDialog.CenteredHeader>{t.FeeHandling}</RsnDialog.CenteredHeader>
            <RsnDialog.Body align={'left'}>
                <p className='fv-text-l pb-4 text-center text-neutral-90'>{t.HowTheTransferFeeAffects}</p>
                <div className='flex flex-col gap-1 rounded-[10px] bg-neutral-20 p-5'>
                    <h4 className={cls.h}>{t.Included}</h4>
                    <p className={cls.p}>{t.IncludedExplanation}</p>
                    <hr className='my-4 h-px bg-neutral-30' />
                    <h4 className={cls.h}>{t.OnTop}</h4>
                    <p className={cls.p}>{t.OnTopExplanation}</p>
                </div>
            </RsnDialog.Body>
            <RsnDialog.Footer column>
                <RsnButton.Alike intent={'secondary'} as={RsnDialog.CloseTrigger}>
                    {t.GotIt}
                </RsnButton.Alike>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

const HintActionUseAll = ({used, onClick}: {used?: boolean; onClick?: () => void}) => {
    return (
        <RsnTextButton.Inline type={'button'} disabled={used} underlined={!used} onClick={onClick} size={'l'}>
            {used ? 'All funds are used' : 'Use all funds'}
        </RsnTextButton.Inline>
    );
};

function FeeIssueDialog({message}: {message: IssueOnFee}) {
    const {onClose} = RsnDialog.useControls();
    const {asset, routes} = useSendTxResources();
    const {currencyCode} = asset.blockchain;

    const navigate = useNavigate();
    const pathToBuy = routes.buy;
    const onBuy =
        pathToBuy !== null &&
        (() => {
            navigate(pathToBuy, {replace: true});
        });

    return (
        <InfoPopup
            type='warning'
            title={t.NotEnough}
            details={(() => {
                switch (message) {
                    case IssueOnFee.NotEnough:
                        return t.NotEnouthForFee({currencyCode});
                    default:
                        return absurd<never>(message);
                }
            })()}
            actions={{
                ...(onBuy && {primary: [t.Buy({currencyCode}), onBuy]}),
                secondary: [t.GotIt, onClose],
            }}
        />
    );
}

function interpolate<T extends string>(template: T) {
    type Holes<T extends string, Acc extends string = never> = T extends `${string}{{${infer K}}}${infer Rest}`
        ? Holes<Rest, Acc | K>
        : Acc;

    return (values: Record<Holes<T>, string>) => {
        let text: string = template;
        for (const key of getKeysUnsafe(values)) {
            text = template.replaceAll(`{{${key}}}`, values[key]);
        }
        return text;
    };
}
